import type { NormalisedHour } from '../daily-obs-types';

export const finiteCoastal = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** Canonical surf scores already use the 0–10 display scale. Zero is valid. */
export function parseSurfScore(value: unknown): number | null {
  return finiteCoastal(value) && value >= 0 && value <= 10 ? value : null;
}

export function parseSurfHourlyScores(value: unknown): { hour: number; score: number | null; isDay: boolean }[] {
  if (!Array.isArray(value)) return [];
  const slots = new Map<number, { hour: number; score: number | null; isDay: boolean }>();
  for (const entry of value.slice(0, 48)) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as Record<string, unknown>;
    if (!finiteCoastal(row.hour) || !Number.isInteger(row.hour) || row.hour < 0 || row.hour > 23
      || typeof row.isDay !== 'boolean' || slots.has(row.hour)) continue;
    slots.set(row.hour, { hour: row.hour, score: parseSurfScore(row.score), isDay: row.isDay });
  }
  return [...slots.values()].sort((a, b) => a.hour - b.hour);
}

/** Validate the fields needed to depict a session before trusting stored JSON. */
export function hasCoastalEvidence(value: unknown): value is NormalisedHour {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const hour = value as Partial<NormalisedHour>;
  const marine = hour.marine;
  return finiteCoastal(hour.hour) && hour.hour >= 0 && hour.hour <= 23
    && !!marine && finiteCoastal(marine.wave_height_m) && marine.wave_height_m >= 0
    && (marine.wave_height_m < 0.1 || (finiteCoastal(marine.wave_period_s) && marine.wave_period_s > 0))
    && !!hour.weather && typeof hour.weather.is_day === 'boolean' && finiteCoastal(hour.weather.wind_speed_10m_kmh) && hour.weather.wind_speed_10m_kmh >= 0
    && finiteCoastal(hour.weather.wind_gust_10m_kmh) && hour.weather.wind_gust_10m_kmh >= 0;
}

/** Retain at most one valid integer hour per clock position; never fabricate missing hours. */
export function parseSurfHours(value: unknown): NormalisedHour[] {
  if (!Array.isArray(value)) return [];
  const slots = new Map<number, NormalisedHour>();
  for (const hour of value.slice(0, 48)) {
    if (hasCoastalEvidence(hour) && Number.isInteger(hour.hour) && !slots.has(hour.hour)) slots.set(hour.hour, hour);
  }
  return [...slots.values()].sort((a, b) => a.hour - b.hour);
}

/** Reject old or invalid optical readings. These are observations, not a safety assessment. */
export function parseSurfWaterQuality(value: unknown, now = Date.now()): { secchiDepthM: number | null; chlorophyllMgM3: number | null } | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const fetched = typeof row.fetched_at === 'string' ? Date.parse(row.fetched_at) : NaN;
  if (!Number.isFinite(fetched) || now - fetched > 48 * 3600000 || fetched - now > 3600000) return null;
  const positive = (v: unknown) => finiteCoastal(v) && v >= 0 ? v : null;
  const secchiDepthM = positive(row.secchi_depth_m), chlorophyllMgM3 = positive(row.chlorophyll_mg_m3);
  return secchiDepthM === null && chlorophyllMgM3 === null ? null : { secchiDepthM, chlorophyllMgM3 };
}
